/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-02-24 15:39:41                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-03-03 15:09:26                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * TechniciansTab: Technicians list view
    * useEffect: get technicians list
*/

/* Imports */
import { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
/***/

/* Components */
import { api } from "../../../services/api/api";
import { Table } from "../../../components/table/Table";
import { Pagination } from "../../../components/pagination/Pagination";
import TechnicianListItem from "../../../components/data/technician/TechnicianListItem";
import ExportButton from "../../../components/export-button/ExportButton";
import { technicianExportDescriptor, TechnicianExportRow } from "../../../services/export/data/technicianExport";
import { t } from "../../../i18n";
import { AutoDiagLatest } from "../../../types/auto-diag/autoDiagSubmission";
import { AutoDiagTechnicianStatus } from "../../../services/auto-diag/autoDiagStatus";
import { useTablePageSize } from "../../../hooks/useTablePageSize";
/***/

/**
* TechniciansTab: Technicians list view
* display list of technicians
*/
export default function TechniciansTab({
  isAdminComponent
}: {
  isAdminComponent?: boolean
}) {
  const [techs, setTechs] = useState<EasyTech.Technician[] | null>(null);
  const [autoDiagByTech, setAutoDiagByTech] = useState<Map<string, AutoDiagLatest>>(new Map());
  const [statusByTech, setStatusByTech] = useState<Map<string, AutoDiagTechnicianStatus>>(new Map());
  const navigate = useNavigate();
  const [pageState, setPageState] = useState<number>(0);
  const [orderState, setOrderState] = useState<{ head: string, orderDesc: boolean }>({ head: "lastName", orderDesc: false });

  const onOrderChange = (head: string, orderDesc: boolean) => {
    setOrderState({ head, orderDesc });
    setPageState(0);
  };
    
  const TABLE_HEAD: {label: string, orderKey: string | null, width?: string}[] = [
    {
      label: t("common:lastName"),
      orderKey: "lastName",
      width: "14%"
    },{
      label: t("common:firstName"),
      orderKey: "firstName",
      width: "13%"
    },{
      label: t("common:email"),
      orderKey: "email",
      width: "19%"
    },{
      label: t("common:phone"),
      orderKey: "phone",
      width: "11%"
    },{
      label: "VAN",
      orderKey: "van",
      width: "13%"
    },{
      // Not sortable: sorting on "never submitted" needs a comparator of its own, and these follow
      // the same rule as the admin-only columns below.
      label: t("common:autoDiag.lastDiag"),
      orderKey: null,
      width: "12%"
    },{
      label: t("common:autoDiag.drivingLicence"),
      orderKey: null,
      width: "6%"
    },{
      label: t("common:roles"),
      orderKey: "roles",
      width: "7%"
    },{
      label: t("common:color"),
      orderKey: "color",
      width: "5%"
    }
  ];

  if (isAdminComponent) {
    TABLE_HEAD.push({label: "APP VERSION", orderKey: null, width: "120px" });
    TABLE_HEAD.push({label: "LAST POSITION UPDATE", orderKey: null, width: "180px" });
    TABLE_HEAD.push({label: t("common:actions"), orderKey: null, width: "80px" });
  }
  const pageSize = useTablePageSize();

  /* Un changement de taille invalide le curseur de page : l'élément 181 est en page 9 à 20 par page
   * et en page 2 à 100. Repartir de la première page est le seul comportement qui ne montre jamais
   * une page vide. */
  useEffect(() => { setPageState(0); }, [pageSize]);

  const loadUsers = useCallback(() => {
    Promise.all([
      api.get("admin/users"),
      api.get("admin/technicians")
    ]).then(([usersData, techsData]) => {
      const allUsers = [...(usersData as EasyTech.User[]), ...(techsData as EasyTech.Technician[])];
      
      const techProfiles = new Map<string, EasyTech.Technician>();
      (techsData as EasyTech.Technician[]).forEach(t => techProfiles.set(t.id, t));

      const uniqueIds = Array.from(new Set(allUsers.map(u => u.id)));
      const techs = uniqueIds
        .map(id => {
          const user = allUsers.find(u => u.id === id)!;
          const techProfile = techProfiles.get(id);
          return { ...user, ...techProfile } as EasyTech.Technician;
        })
        .filter(u => u.roles.includes("ROLE_TECH"));

      if (isAdminComponent) {
        api.get("admin/technician_devices")
          .then((ret2) => {
            const devices = ret2 as EasyTech.TechnicianDevice[];
            api.get("admin/technician_locations")
              .then((ret3) => {
                const locations = ret3 as EasyTech.TechnicianPosition[];
                techs.forEach((tech) => {
                  devices.forEach((device) => {
                    if (device.technician && tech.id === device.technician.id) {
                      delete device.technician;
                      tech.device = device;
                    } 
                  });
                  locations.forEach((location) => {
                    if (location.technician && tech.id === location.technician.id) {
                      delete location.technician;
                      tech.location = location;
                    } 
                  });
                });
                setTechs([...techs]);
              }).catch((err) => {
                console.error(err);
                setTechs(techs);
              });
          }).catch((err) => {
            console.error(err);
            setTechs(techs);
          });
      } else {
        setTechs(techs);
      }
    })
    .catch((err) => {
      console.error(err);
      setTechs([]);
    });
  }, [isAdminComponent]);

  /**
  * useEffect: get users list
  * on init
  */
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);
  /***/

  /**
  * useEffect: get the technicians who are not up to date with their Auto Diag
  *
  * The endpoint returns only non-current technicians, so anyone absent from it is up to date.
  * Degrades like the summary below: no warning rather than no list.
  */
  useEffect(() => {
    api.get("admin/auto-diag/technicians")
      .then((ret) => setStatusByTech(
        new Map((ret as AutoDiagTechnicianStatus[]).map((row) => [row.technicianId, row]))))
      .catch((err) => {
        console.error(err);
        setStatusByTech(new Map());
      });
  }, []);
  /***/

  /**
  * useEffect: get each technician's most recent Auto Diag record
  *
  * A decoration on a table that works without it, so a failure leaves both columns empty rather
  * than taking the list down — the same way the device and location lookups already degrade.
  * Technicians absent from the response have never submitted.
  */
  useEffect(() => {
    api.get("admin/auto-diag/latest")
      .then((ret) => setAutoDiagByTech(
        new Map((ret as AutoDiagLatest[]).map((row) => [row.technicianId, row]))))
      .catch((err) => {
        console.error(err);
        setAutoDiagByTech(new Map());
      });
  }, []);
  /***/

  const sortedTechs = useMemo(() => {
    if (!techs) return null;
    return [...techs].sort((a: any, b: any) => {
      let valA: string = "";
      let valB: string = "";

      if (orderState.head === "phone" || orderState.head === "color") {
        valA = a.attributes?.[orderState.head as keyof EasyTech.TechnicianAttributes] || "";
        valB = b.attributes?.[orderState.head as keyof EasyTech.TechnicianAttributes] || "";
      } else if (orderState.head === "van") {
        valA = a.van?.name || "";
        valB = b.van?.name || "";
      } else {
        const key = orderState.head as keyof EasyTech.Technician;
        valA = (a[key] != null ? String(a[key]) : "");
        valB = (b[key] != null ? String(b[key]) : "");
      }
      
      if (Array.isArray(valA)) valA = valA.join(", ");
      if (Array.isArray(valB)) valB = valB.join(", ");

      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();

      return orderState.orderDesc 
        ? strB.localeCompare(strA)
        : strA.localeCompare(strB);
    });
  }, [techs, orderState]);

  const pagedTechs = useMemo(() => {
    if (!sortedTechs) return null;
    return sortedTechs.slice(pageState * pageSize, (pageState + 1) * pageSize);
  }, [sortedTechs, pageState, pageSize]);

  const totalPages = useMemo(() => {
    if (!sortedTechs) return 0;
    return Math.ceil(sortedTechs.length / pageSize);
  }, [sortedTechs, pageSize]);

  return (
    <div className="my-4 relative min-h-48">

      {pagedTechs &&
        <>
          {/*
            * `rows` rather than a search, and `sortedTechs` rather than `pagedTechs`: see UsersTab.
            * Its own descriptor, because this table shows van, phone and colour, and the first two
            * live under `attributes` where a plain user has nothing.
            */}
          <div className="flex justify-end mb-2">
            {/* Rows carry the Auto Diag summary so the export mirrors the two columns above. */}
            <ExportButton<TechnicianExportRow>
              entityName="technicians"
              fileName="technicians.xlsx"
              descriptor={technicianExportDescriptor}
              rows={(sortedTechs ?? []).map((tech) => ({ ...tech, autoDiag: autoDiagByTech.get(tech.id) }))}
            />
          </div>

          <Table  tableHead={TABLE_HEAD}
                  emptyTable={(!pagedTechs || pagedTechs.length === 0) ? "No User" : false}
                  selectedHead={orderState.head}
                  orderDesc={orderState.orderDesc}
                  onHeadClick={onOrderChange}
                  enableRowLinks
                  >
            {pagedTechs.map((tech: EasyTech.Technician, index: number) => (
              <TechnicianListItem key={tech.id}
                                  tech={tech}
                                  autoDiag={autoDiagByTech.get(tech.id)}
                                  autoDiagStatus={statusByTech.get(tech.id)}
                                  onClick={() => navigate(`/users/${tech.id}?userType=TECHNICIAN`)}
                                  isLast={index === pagedTechs.length - 1}
                                  onChange={loadUsers}
                                  isAdminComponent={isAdminComponent === undefined ? false : isAdminComponent} />
            ))}
          </Table>
          
          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                actualPage={pageState}
                pageNumber={totalPages}
                changePage={setPageState}
              />
            </div>
          )}
        </>
      }
    </div>
  );
}
